import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import TrapLink from "./TrapLink";
import TruncatedSeq from "../../../../components/TruncatedSeq";
import GeneSymbols from "./GeneSymbols";
import DbnsfpRsIds from "./DbnsfpRsIds";
import HgvsTranscript from "./HgvsTranscript";
import MastermindLinks from "./MastermindLinks";
import { getPositionUrl } from "./helpers";
import {
  getSchemaProperties,
  getPosition,
  getChromosome,
  getRef,
  getAlt,
  getVariantId,
  getVariantType,
  getIsFLT3ITD,
  getVariantCytobands,
  getBlosumScore,
} from "../../../../state/selectors/general.selectors";
import flagService from "../../../../services/flagService";
import store from "../../../../state/store";
import isPortal from "../../../../common/helpers/isPortal";
import HelperText from "../../../../components/atoms/HelperText";
import HelperTextLabel from "../../../../components/atoms/HelperText/HelperTextLabel";

const { shape, string, number, bool } = PropTypes;
const isClinical = isPortal();

const VariantDetailsContent = ({
  schemaProperties,
  chromosome,
  pos,
  alt,
  reference,
  variantId,
  variantType,
  isFLT3ITD,
  cytobands,
}) => {
  useEffect(() => {
    if (isClinical) {
      flagService.register(store);
    }
  }, []);

  if (schemaProperties && variantId) {
    return (
      <div className="varsome-horiz-flex">
        <div className="varsome-horiz-list-item tw-p-0">
          <div className="varsome-horiz-flex">
            <div className="varsome-horiz-list-item tw-pb-0">
              <div className="varsome-horiz-list-label">
                {schemaProperties.chromosome.label}
              </div>
              <div className="varsome-horiz-list-value">{chromosome}</div>
            </div>
            <div className="varsome-horiz-list-item tw-pb-0">
              <div className="varsome-horiz-list-label">
                {schemaProperties.pos.label}
              </div>
              <div className="varsome-horiz-list-value">{pos}</div>
            </div>
          </div>
          <small>
            <a
              href={getPositionUrl(chromosome, pos, reference, variantId)}
              target="_blank"
            >
              <i className="fa fa-external-link" />
              {" UCSC genome browser "}
            </a>
            <HelperText fieldDescriptionKey="UCSC genome browser" />
          </small>
          <MastermindLinks />
          <TrapLink />
        </div>
        <div className="varsome-horiz-list-item">
          <HelperTextLabel
            label={schemaProperties.ref.label}
            fieldDescriptionKey="REF sequence"
          />
          <div className="varsome-horiz-list-value">
            <TruncatedSeq seq={reference} />
          </div>
        </div>
        <div className="varsome-horiz-list-item">
          <HelperTextLabel
            label={schemaProperties.alt.label}
            fieldDescriptionKey="ALT sequence"
          />
          <div className="varsome-horiz-list-value">
            <TruncatedSeq seq={alt} />
          </div>
        </div>
        {variantType && (
          <div className="varsome-horiz-list-item">
            <HelperTextLabel
              label={schemaProperties.variant_type.label}
              fieldDescriptionKey="Variant type"
            />
            <div className="varsome-horiz-list-value">
              {isFLT3ITD ? "ITD" : variantType}
            </div>
          </div>
        )}
        {cytobands && (
          <div className="varsome-horiz-list-item">
            <HelperTextLabel
              label={schemaProperties.cytobands.label}
              fieldDescriptionKey="Cytoband"
            />
            <div className="varsome-horiz-list-value">{cytobands}</div>
          </div>
        )}
        <HgvsTranscript />
        <DbnsfpRsIds />
        <GeneSymbols />
      </div>
    );
  }
  return null;
};

VariantDetailsContent.propTypes = {
  schemaProperties: shape({}),
  chromosome: string,
  pos: number,
  reference: string,
  alt: string,
  variantId: string,
  variantType: string,
  isFLT3ITD: bool,
  cytobands: string,
};

VariantDetailsContent.defaultProps = {
  schemaProperties: null,
  chromosome: null,
  pos: null,
  reference: null,
  alt: null,
  variantId: null,
  variantType: null,
  isFLT3ITD: false,
  cytobands: null,
};

const mapStateToProps = (state) => ({
  schemaProperties: getSchemaProperties(state),
  chromosome: getChromosome(state),
  pos: getPosition(state),
  reference: getRef(state),
  alt: getAlt(state),
  variantId: getVariantId(state),
  variantType: getVariantType(state),
  isFLT3ITD: getIsFLT3ITD(state),
  cytobands: getVariantCytobands(state),
  blosumScore: getBlosumScore(state),
});

export default connect(mapStateToProps)(VariantDetailsContent);
